import * as THREE from 'three'
import { Line2 } from 'three/addons/lines/Line2.js'
import { LineMaterial } from 'three/addons/lines/LineMaterial.js'
import { LineGeometry } from 'three/addons/lines/LineGeometry.js'

// Append a sample point only when the reticle has moved at least this far
// from the last sample. Smooths out tiny jitter and keeps geometry small.
const SAMPLE_THRESHOLD_M = 0.03

// Hard cap on how many vertices a single stroke holds. ~16m of 3cm samples.
const MAX_POINTS_PER_STROKE = 500

// Visual style
const STROKE_COLOR = 0x00e5ff
const STROKE_LINEWIDTH_PX = 8
const ACTIVE_STROKE_COLOR = 0xfff64f
const VERTEX_MARKER_RADIUS = 0.025

let _scene = null
let _sketchGroup = null
let _committed = []
let _active = null // { mesh, material, geometry, points: Vector3[] }
let _drawing = false

export function initSketch(scene) {
  _scene = scene
  _sketchGroup = new THREE.Group()
  _sketchGroup.name = 'sketches'
  // Render on top of scene depth so the line stays visible against the
  // floor even when slightly intersecting it.
  _sketchGroup.renderOrder = 500
  scene.add(_sketchGroup)
}

export function startStroke(initialPoint) {
  if (_drawing) endStroke(initialPoint)

  const geometry = new LineGeometry()
  geometry.setPositions([
    initialPoint.x,
    initialPoint.y,
    initialPoint.z,
    initialPoint.x,
    initialPoint.y,
    initialPoint.z
  ])

  const material = new LineMaterial({
    color: ACTIVE_STROKE_COLOR,
    linewidth: STROKE_LINEWIDTH_PX,
    worldUnits: false,
    depthTest: false,
    transparent: true,
    resolution: new THREE.Vector2(window.innerWidth, window.innerHeight)
  })

  const mesh = new Line2(geometry, material)
  mesh.renderOrder = 501
  mesh.computeLineDistances()
  _sketchGroup.add(mesh)

  // Visible "you started a stroke here" marker. Always renders (depthTest
  // off, high renderOrder) so the user gets immediate feedback even before
  // the line itself becomes visible (it's zero-length on the first frame).
  const markerStart = buildMarker(initialPoint, ACTIVE_STROKE_COLOR)
  _sketchGroup.add(markerStart)

  _active = {
    mesh,
    material,
    geometry,
    points: [initialPoint.clone()],
    markers: [markerStart]
  }
  _drawing = true
}

// Called every frame from the AR loop while `isDrawing()` is true.
// Appends a sample if the reticle has moved enough.
export function tickStroke(currentPoint) {
  if (!_drawing || !_active) return

  const last = _active.points[_active.points.length - 1]
  if (currentPoint.distanceTo(last) >= SAMPLE_THRESHOLD_M) {
    if (_active.points.length < MAX_POINTS_PER_STROKE) {
      _active.points.push(currentPoint.clone())
    } else {
      // Soft cap — overwrite the last point so the line tracks the reticle
      // without growing further. Keeps the stroke alive but bounded.
      _active.points[_active.points.length - 1] = currentPoint.clone()
    }
    refreshActiveGeometry()
  } else if (_active.points.length === 1) {
    // Special-case the very first sample after stroke start: keep the
    // trailing endpoint pinned to the reticle so the line is non-zero
    // length immediately, even before we cross the sample threshold.
    const flat = pointsToFlatArray(_active.points)
    flat.push(currentPoint.x, currentPoint.y, currentPoint.z)
    _active.geometry.setPositions(flat)
    _active.mesh.computeLineDistances()
  } else {
    // Even when not appending, animate the trailing endpoint so the line
    // visibly follows the reticle in real time.
    const flat = pointsToFlatArray(_active.points)
    flat[flat.length - 3] = currentPoint.x
    flat[flat.length - 2] = currentPoint.y
    flat[flat.length - 1] = currentPoint.z
    _active.geometry.setPositions(flat)
    _active.mesh.computeLineDistances()
  }
}

export function endStroke(finalPoint) {
  if (!_drawing || !_active) return

  if (finalPoint) {
    _active.points.push(finalPoint.clone())
    refreshActiveGeometry()
  }

  // Switch the committed stroke to its resting color. Keep depthTest off
  // so the line stays visible even when slightly overlapping the floor.
  _active.material.color = new THREE.Color(STROKE_COLOR)
  _active.material.needsUpdate = true
  for (const marker of _active.markers) {
    marker.material.color = new THREE.Color(STROKE_COLOR)
    marker.material.needsUpdate = true
  }

  // Add an end-marker at the final point so committed strokes show both
  // endpoints clearly.
  const finalP = _active.points[_active.points.length - 1]
  const markerEnd = buildMarker(finalP, STROKE_COLOR)
  _sketchGroup.add(markerEnd)
  _active.markers.push(markerEnd)

  _committed.push(_active)
  _active = null
  _drawing = false
}

export function clearSketches() {
  for (const stroke of _committed) {
    disposeStroke(stroke)
  }
  _committed = []

  if (_active) {
    disposeStroke(_active)
    _active = null
  }
  _drawing = false
}

function disposeStroke(stroke) {
  _sketchGroup.remove(stroke.mesh)
  stroke.geometry.dispose()
  stroke.material.dispose()
  for (const marker of stroke.markers || []) {
    _sketchGroup.remove(marker)
    marker.geometry.dispose()
    marker.material.dispose()
  }
}

export function isDrawing() {
  return _drawing
}

export function strokeCount() {
  return _committed.length + (_drawing ? 1 : 0)
}

// Update the line material's resolution on viewport resize. Without this,
// stroke widths drift after rotation / window changes.
export function onResize(width, height) {
  for (const stroke of _committed) {
    stroke.material.resolution.set(width, height)
  }
  if (_active) {
    _active.material.resolution.set(width, height)
  }
}

function refreshActiveGeometry() {
  if (!_active) return
  const flat = pointsToFlatArray(_active.points)
  _active.geometry.setPositions(flat)
  _active.mesh.computeLineDistances()
}

function pointsToFlatArray(points) {
  const out = new Array(points.length * 3)
  for (let i = 0; i < points.length; i++) {
    const p = points[i]
    out[i * 3] = p.x
    out[i * 3 + 1] = p.y
    out[i * 3 + 2] = p.z
  }
  return out
}

function buildMarker(point, color) {
  const geometry = new THREE.SphereGeometry(VERTEX_MARKER_RADIUS, 16, 12)
  const material = new THREE.MeshBasicMaterial({
    color,
    depthTest: false,
    transparent: true
  })
  const mesh = new THREE.Mesh(geometry, material)
  mesh.position.copy(point)
  mesh.renderOrder = 502
  return mesh
}
